import React, { useMemo } from 'react';
import { Download } from 'react-feather';
import moment from 'moment';

export function computeMatchMetrics(processedData, processedBackendData, reportStatistics) {
  const breakdownTotal =
    (processedData?.breakdown?.total_records || 0) +
    (processedData?.breakdown?.dsp_records || 0) +
    (processedData?.breakdown?.territory_records || 0) +
    (processedData?.breakdown?.sale_type_records || 0);
  const backendTotalRecords = processedBackendData?.metadata?.total_records || 0;
  const recordsMatch =
    breakdownTotal > 0 && backendTotalRecords > 0 && breakdownTotal === backendTotalRecords;

  const backendRevenueRaw =
    reportStatistics?.database_totals?.total_revenue ||
    processedBackendData?.metadata?.total_revenue ||
    null;
  const s3RevenueRaw = processedData?.metadata?.total_revenue || null;
  const backendRevenue = backendRevenueRaw !== null ? Number(backendRevenueRaw) : null;
  const s3Revenue = s3RevenueRaw !== null ? Number(s3RevenueRaw) : null;
  const revenueMatch =
    backendRevenue !== null &&
    s3Revenue !== null &&
    !Number.isNaN(backendRevenue) &&
    !Number.isNaN(s3Revenue) &&
    Math.abs(backendRevenue - s3Revenue) < 0.01;

  const backendStreams =
    reportStatistics?.database_totals?.total_streams ||
    processedBackendData?.metadata?.total_streams ||
    null;
  const s3Streams = processedData?.metadata?.total_streams || null;
  const streamsMatch =
    backendStreams !== null && s3Streams !== null && backendStreams === s3Streams;

  const backendDownloads =
    reportStatistics?.database_totals?.total_downloads ||
    processedBackendData?.metadata?.total_downloads ||
    null;
  const s3Downloads = processedData?.metadata?.total_downloads || null;
  const downloadsMatch =
    backendDownloads !== null && s3Downloads !== null && backendDownloads === s3Downloads;

  return [
    { key: 'records', label: 'Records', match: recordsMatch },
    { key: 'revenue', label: 'Revenue', match: revenueMatch },
    { key: 'streams', label: 'Streams', match: streamsMatch },
    { key: 'downloads', label: 'Downloads', match: downloadsMatch },
  ];
}

function scoreTone(score) {
  if (score === 10) return 'high';
  if (score >= 7) return 'mid';
  if (score >= 5) return 'warn';
  return 'low';
}

export default function ProcessingReportHeader({
  report,
  comparisonScore,
  reportStatistics,
  onDownloadFile,
}) {
  const stats = useMemo(() => {
    if (!reportStatistics) return [];
    return [
      { label: 'Rows', value: reportStatistics.total_rows },
      { label: 'Processed', value: reportStatistics.processed_rows },
      { label: 'Saved', value: reportStatistics.saved_entries },
      { label: 'Skipped', value: reportStatistics.skipped_entries },
      { label: 'Errors', value: reportStatistics.error_entries },
    ].filter((s) => s.value !== undefined && s.value !== null);
  }, [reportStatistics]);

  const tone = scoreTone(comparisonScore.score);

  return (
    <>
      <div className="pr-toolbar">
        <div className="pr-toolbar__info">
          <h1 className="pr-toolbar__title">{report?.provider_name || 'Processing Report'}</h1>
          <p className="pr-toolbar__meta">
            <span>#{report?.id}</span>
            {report?.created_at && (
              <>
                <span className="pr-toolbar__dot">·</span>
                <span>{moment(report.created_at).format('MMM D, YYYY HH:mm')}</span>
              </>
            )}
            {report?.report_status && (
              <>
                <span className="pr-toolbar__dot">·</span>
                <span className={`inc-status inc-status--${report.report_status === 'available' ? 'done' : 'pending'}`}>
                  {report.report_status}
                </span>
              </>
            )}
          </p>
        </div>

        <div className="pr-toolbar__right">
          <span className={`inc-score inc-score--${tone}`} title="Backend vs S3 comparison">
            {comparisonScore.score}/{comparisonScore.total}
          </span>
          {onDownloadFile && (
            <button type="button" className="inc-file-btn" onClick={onDownloadFile} title="Download raw file">
              <Download size={15} />
            </button>
          )}
        </div>
      </div>

      {stats.length > 0 && (
        <div className="inc-stats">
          {stats.map((stat) => (
            <div key={stat.label} className="inc-stat">
              <p className="inc-stat__label">{stat.label}</p>
              <p className="inc-stat__value">{Number(stat.value).toLocaleString()}</p>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
